import React, { useState, useEffect, useCallback } from "react";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Database,
  Loader,
  AlertCircle,
  RefreshCw,
  Filter,
  Eye,
  EyeOff,
  X,
} from "lucide-react";
import PropTypes from "prop-types";
import axiosClient from "../../../../api-client/AxiosClient";
import axiosClientPayload from "../../../../api-client/AxiosClientPayload";

const API_CODE = process.env.REACT_APP_API_CODE;


const toast = {
  error: (msg) => console.error(msg),
  warning: (msg) => console.warn(msg),
  info: (msg) => console.info(msg),
  success: (msg) => console.log(msg),
};

const storedConfig = localStorage.getItem("organization_config_id");

// Custom Node Components
const ObjectNode = ({ data }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="rounded-full border-4 border-orange-500 bg-gradient-to-br from-orange-100 to-orange-200 shadow-2xl flex items-center justify-center relative hover:shadow-3xl transition-all cursor-pointer"
      style={{ width: "160px", height: "160px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {createCircularHandles("target", "#fb923c", 160)}
      {createCircularHandles("source", "#fb923c", 160)}

      <div className="text-center px-3 py-2 flex flex-col items-center justify-center h-full">
        <div className="text-sm font-bold text-gray-900 leading-tight">
          {(() => {
            const { firstLine, secondLine } = splitTextForCircle(
              data.label,
              10
            );
            return (
              <>
                <div className="break-words">{firstLine}</div>
                {secondLine && (
                  <div className="break-words mt-0.5">{secondLine}</div>
                )}
              </>
            );
          })()}
        </div>
        <div className="text-xs text-gray-700 mt-1 font-semibold uppercase">
          Object
        </div>
      </div>

      {isHovered && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-4 bg-gray-900 text-white px-4 py-3 rounded-lg shadow-2xl z-50 min-w-[200px]">
          <div className="text-xs font-bold text-orange-300">
            📦 Central Object
          </div>
        </div>
      )}
    </div>
  );
};

ObjectNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

// Helper: find best split index at capital letter near midpoint
const findBestCapitalSplit = (text, midPoint) => {
  const capitalPositions = [];
  for (let i = 1; i < text.length; i++) {
    const c = text[i];
    if (c === c.toUpperCase() && c !== c.toLowerCase()) {
      capitalPositions.push(i);
    }
  }
  let bestSplitIndex = -1;
  let minDistance = Infinity;
  for (const pos of capitalPositions) {
    const distance = Math.abs(pos - midPoint);
    if (distance < minDistance && pos > 0 && pos < text.length) {
      minDistance = distance;
      bestSplitIndex = pos;
    }
  }
  return bestSplitIndex > 0 && minDistance < 10 ? bestSplitIndex : -1;
};

// Helper: split by delimiter index near midpoint
const splitByDelimiterNearMid = (text, midPoint, delimiter) => {
  const lastIdx = text.lastIndexOf(delimiter, midPoint);
  const firstIdx = text.indexOf(delimiter, midPoint);
  const useLast = lastIdx > 0 && midPoint - lastIdx < 5;
  const useFirst = firstIdx > midPoint && firstIdx - midPoint < 5;
  if (useLast) {
    const first = text.substring(0, lastIdx);
    const second = text.substring(lastIdx + 1);
    return delimiter === "_"
      ? {
          firstLine: first.replaceAll("_", " "),
          secondLine: second.replaceAll("_", " "),
        }
      : { firstLine: first, secondLine: second };
  }
  if (useFirst) {
    const first = text.substring(0, firstIdx);
    const second = text.substring(firstIdx + 1);
    return delimiter === "_"
      ? {
          firstLine: first.replaceAll("_", " "),
          secondLine: second.replaceAll("_", " "),
        }
      : { firstLine: first, secondLine: second };
  }
  return null;
};

// Helper function to split text into two lines for circular nodes
const splitTextForCircle = (text, maxCharsPerLine = 12) => {
  if (!text) return { firstLine: "", secondLine: "" };
  if (text.length <= maxCharsPerLine) {
    return { firstLine: text, secondLine: "" };
  }
  const midPoint = Math.floor(text.length / 2);
  const bestSplitIndex = findBestCapitalSplit(text, midPoint);
  if (bestSplitIndex >= 0) {
    return {
      firstLine: text.substring(0, bestSplitIndex).trim(),
      secondLine: text.substring(bestSplitIndex).trim(),
    };
  }
  const byUnderscore = splitByDelimiterNearMid(text, midPoint, "_");
  if (byUnderscore) {
    return {
      firstLine: byUnderscore.firstLine.trim(),
      secondLine: byUnderscore.secondLine.trim(),
    };
  }
  const bySpace = splitByDelimiterNearMid(text, midPoint, " ");
  if (bySpace) {
    return {
      firstLine: bySpace.firstLine.trim(),
      secondLine: bySpace.secondLine.trim(),
    };
  }
  return {
    firstLine: text.substring(0, midPoint).trim(),
    secondLine: text.substring(midPoint).trim(),
  };
};

// 2. ADD THIS HELPER FUNCTION after splitTextForCircle
// Helper function to create handles around circular node at 8 positions
const createCircularHandles = (handleType, color, nodeSize = 150) => {
  const handleCount = 8;
  const radius = nodeSize / 2;
  const handles = [];

  for (let i = 0; i < handleCount; i++) {
    const angle = (i / handleCount) * Math.PI * 2 - Math.PI / 2;
    const x = radius + radius * Math.cos(angle);
    const y = radius + radius * Math.sin(angle);

    handles.push(
      <Handle
        key={`${handleType}-${i}`}
        id={`${handleType}-${i}`}
        type={handleType}
        style={{
          background: color,
          width: 10,
          height: 10,
          border: `2px solid white`,
          left: `${x}px`,
          top: `${y}px`,
          position: "absolute",
          transform: "translate(-50%, -50%)",
          opacity: 0,
        }}
      />
    );
  }
  return handles;
};

const TargetNode = ({ data }) => {
  return (
    <div
      className="rounded-full border-3 bg-gradient-to-br from-cyan-100 to-cyan-200 border-cyan-500 shadow-lg hover:shadow-2xl transition-all flex items-center justify-center cursor-pointer"
      style={{ width: "130px", height: "130px" }}
    >
      {createCircularHandles("target", "#06b6d4", 130)}
      {createCircularHandles("source", "#06b6d4", 130)}

      <div className="text-center px-2 py-1 flex flex-col items-center justify-center h-full">
        <div className="text-xs font-bold text-gray-900 leading-tight">
          {(() => {
            const { firstLine, secondLine } = splitTextForCircle(data.label, 8);
            return (
              <>
                <div className="break-words">{firstLine}</div>
                {secondLine && (
                  <div className="break-words mt-0.5">{secondLine}</div>
                )}
              </>
            );
          })()}
        </div>
        <div className="text-xs text-gray-700 mt-1 font-semibold">Target</div>
      </div>
    </div>
  );
};

TargetNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

const SourceNode = ({ data }) => {
  return (
    <div
      className="rounded-full border-3 bg-gradient-to-br from-green-100 to-green-200 border-green-500 shadow-lg hover:shadow-2xl transition-all flex items-center justify-center cursor-pointer"
      style={{ width: "130px", height: "130px" }}
    >
      {createCircularHandles("target", "#10b981", 130)}
      {createCircularHandles("source", "#10b981", 130)}

      <div className="text-center px-2 py-1 flex flex-col items-center justify-center h-full">
        <div className="text-xs font-bold text-gray-900 leading-tight">
          {(() => {
            const { firstLine, secondLine } = splitTextForCircle(data.label, 8);
            return (
              <>
                <div className="break-words">{firstLine}</div>
                {secondLine && (
                  <div className="break-words mt-0.5">{secondLine}</div>
                )}
              </>
            );
          })()}
        </div>
        <div className="text-xs text-gray-700 mt-1 font-semibold">Source</div>
      </div>
    </div>
  );
};

SourceNode.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

const CommonColumnEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  label,
  markerEnd,
  style,
  data,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const edgePath = `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
  const labelX = (sourceX + targetX) / 2;
  const labelY = (sourceY + targetY) / 2;

  return (
    <g>
      {/* Invisible wider path for hover detection */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        aria-label="View common columns"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsHovered((prev) => !prev);
          }
        }}
      />

      {/* Visible edge path */}
      <path
        id={id}
        style={style}
        className="react-flow__edge-path"
        d={edgePath}
        markerEnd={markerEnd}
        fill="none"
        pointerEvents="none"
      />

      {/* Edge label */}
      <text pointerEvents="none">
        <textPath
          href={`#${id}`}
          style={{ fontSize: 10, fill: "#6b21a8", fontWeight: 600 }}
          startOffset="50%"
          textAnchor="middle"
        >
          {label}
        </textPath>
      </text>

      {/* Tooltip */}
      {isHovered && data?.commonColumns && (
        <foreignObject
          width={250}
          height={Math.min(data.commonColumns.length * 20 + 60, 200)}
          x={labelX + 20}
          y={labelY - 100}
          style={{ overflow: "visible", pointerEvents: "none" }}
        >
          <div
            className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-2xl text-xs"
            style={{
              maxHeight: "180px",
              overflowY: "auto",
              pointerEvents: "none",
            }}
          >
            <div className="font-bold text-purple-300 mb-2">
              Common Columns ({data.commonColumns.length}):
            </div>
            <ul className="space-y-1">
              {data.commonColumns.map((col) => (
                <li key={col} className="text-gray-200">
                  • {col}
                </li>
              ))}
            </ul>
          </div>
        </foreignObject>
      )}
    </g>
  );
};

CommonColumnEdge.propTypes = {
  id: PropTypes.string.isRequired,
  sourceX: PropTypes.number.isRequired,
  sourceY: PropTypes.number.isRequired,
  targetX: PropTypes.number.isRequired,
  targetY: PropTypes.number.isRequired,
  label: PropTypes.string,
  markerEnd: PropTypes.object,
  style: PropTypes.object,
  data: PropTypes.shape({
    commonColumns: PropTypes.arrayOf(PropTypes.string),
  }),
};

const nodeTypes = {
  object: ObjectNode,
  target: TargetNode,
  source: SourceNode,
};

const edgeTypes = {
  commonColumn: CommonColumnEdge,
};

const FilterPanel = ({
  filters,
  setFilters,
  onClose,
}) => (
  <div className="absolute top-20 right-6 bg-white rounded-lg shadow-xl border border-gray-200 z-40 w-64">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-blue-600" />
        <h3 className="font-semibold text-sm text-gray-900">Filter Graph</h3>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="p-1 hover:bg-gray-100 rounded transition-colors"
        aria-label="Close filter panel"
      >
        <X className="w-4 h-4 text-gray-500" />
      </button>
    </div>

    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <label htmlFor="filter-targets" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
          <div className="w-5 h-5 rounded bg-cyan-100 border-2 border-cyan-500 flex-shrink-0" />
          <span>Target Structures</span>
        </label>
        <button
          id="filter-targets"
          type="button"
          role="switch"
          aria-checked={filters.showTargets}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              showTargets: !prev.showTargets,
            }))
          }
          className={`p-1.5 rounded transition-colors ${
            filters.showTargets
              ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
          }`}
        >
          {filters.showTargets ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="filter-in-scope" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
          <div
            className={`w-5 h-5 rounded border-2 flex-shrink-0 ${
              filters.showInScopeOnly
                ? "bg-blue-100 border-blue-500"
                : "bg-gray-100 border-gray-300"
            }`}
          />
          <span className={filters.showTargets ? "" : "text-gray-400"}>
            In-Scope Only
          </span>
        </label>
        <button
          id="filter-in-scope"
          type="button"
          role="switch"
          aria-checked={filters.showInScopeOnly}
          aria-disabled={!filters.showTargets}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              showInScopeOnly: !prev.showInScopeOnly,
            }))
          }
          disabled={!filters.showTargets}
          className={`p-1.5 rounded transition-colors ${
            filters.showTargets
              ? filters.showInScopeOnly
                ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
                : "bg-gray-100 text-gray-400 hover:bg-gray-200"
              : "bg-gray-100 text-gray-300 cursor-not-allowed"
          }`}
        >
          {filters.showInScopeOnly ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="filter-sources" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
          <div className="w-5 h-5 rounded bg-green-100 border-2 border-green-500 flex-shrink-0" />
          <span>Source Tables</span>
        </label>
        <button
          id="filter-sources"
          type="button"
          role="switch"
          aria-checked={filters.showSources}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              showSources: !prev.showSources,
            }))
          }
          className={`p-1.5 rounded transition-colors ${
            filters.showSources
              ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
          }`}
        >
          {filters.showSources ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="filter-common-cols" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer flex-1">
          <div className="w-5 h-5 rounded bg-purple-100 border-2 border-purple-500 flex-shrink-0" />
          <span>Common Columns</span>
        </label>
        <button
          id="filter-common-cols"
          type="button"
          role="switch"
          aria-checked={filters.showCommonColumns}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              showCommonColumns: !prev.showCommonColumns,
            }))
          }
          className={`p-1.5 rounded transition-colors ${
            filters.showCommonColumns
              ? "bg-blue-100 text-blue-600 hover:bg-blue-200"
              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
          }`}
        >
          {filters.showCommonColumns ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </button>
      </div>

      <div className="pt-3 border-t border-gray-200">
        <button
          type="button"
          onClick={() =>
            setFilters({
              showTargets: true,
              showSources: true,
              showCommonColumns: true,
              showInScopeOnly: false,
            })
          }
          className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
        >
          Reset All Filters
        </button>
      </div>
    </div>
  </div>
);

FilterPanel.propTypes = {
  filters: PropTypes.shape({
    showTargets: PropTypes.bool,
    showSources: PropTypes.bool,
    showCommonColumns: PropTypes.bool,
    showInScopeOnly: PropTypes.bool,
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

const DataLineageViewers = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState(null);
  const [selectedCorporation, setSelectedCorporation] = useState(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    showTargets: true,
    showSources: true,
    showCommonColumns: true,
    showInScopeOnly: false,
  });

  // Load selected file data from storage
  useEffect(() => {
    const loadSelectedFile = () => {
      try {
        // Get file data from sessionStorage
        const fileDataStr = sessionStorage.getItem("selectedFileData");
        const corporationStr = localStorage.getItem("selectedCorporation");

        if (fileDataStr) {
          const fileData = JSON.parse(fileDataStr);
          setSelectedFileData(fileData);
          console.log("Loaded file data:", fileData);
        } else {
          setError(
            "No file selected. Please select a file from the mapping table."
          );
        }

        if (corporationStr) {
          const corporation = JSON.parse(corporationStr);
          setSelectedCorporation(corporation);
          console.log("Loaded corporation:", corporation);
        }
      } catch (err) {
        console.error("Error loading file data:", err);
        setError("Failed to load file data from storage");
      }
    };

    loadSelectedFile();
  }, []);

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (
        e.key === "selectedFileData" ||
        e.detail?.key === "selectedFileData"
      ) {
        const newValue = e.newValue || e.detail?.newValue;
        if (newValue) {
          try {
            const fileData =
              typeof newValue === "string" ? JSON.parse(newValue) : newValue;
            setSelectedFileData(fileData);
            setError(null);
            console.log("File changed, reloading:", fileData);
          } catch (err) {
            console.error("Error parsing file data:", err);
          }
        }
      }
    };

    // Filter Panel Component

    const handleCustomStorageChange = (e) => {
      if (e.detail?.key === "selectedFileId") {
        // Reload file data when fileId changes
        const fileDataStr = sessionStorage.getItem("selectedFileData");
        if (fileDataStr) {
          try {
            const fileData = JSON.parse(fileDataStr);
            setSelectedFileData(fileData);
            setError(null);
            console.log("File ID changed, reloading:", fileData);
          } catch (err) {
            console.error("Error parsing file data:", err);
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("customStorageChange", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "customStorageChange",
        handleCustomStorageChange
      );
    };
  }, []);

  // Fetch mapper file details when file data changes
  useEffect(() => {
    if (selectedFileData?.fileID) {
      fetchMapperFileDetails(selectedFileData.fileID, selectedFileData);
    }
  }, [selectedFileData]);

  const fetchCommonColumns = async (storedConfig, mappingFileId) => {
    try {
      const requestBody = {
        organization_config_id: storedConfig.toString(),
        mapping_file_id: mappingFileId.toString(),
         token : localStorage.getItem("token") || "",
      };

      console.log("Fetching common columns with:", requestBody);

      const response = await axiosClientPayload.post(
        `https://func-bluegeckoapi-dev-weu-djb8b9h0d0a7g2b0.westeurope-01.azurewebsites.net/api/api/common-columns?code=${API_CODE}`,
        {
          organization_config_id: storedConfig.toString(),
          mapping_file_id: mappingFileId.toString(),
          token : localStorage.getItem("token") || "",

        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "Failed to fetch common columns:",
          response.status,
          errorText
        );
        return [];
      }

      const data = await response.json();
      console.log("Common columns data received:", data);

      if (data && data.common_columns_pairs) {
        return data.common_columns_pairs;
      }
      return [];
    } catch (err) {
      console.error("Error fetching common columns:", err);
      return [];
    }
  };

  const fetchMapperFileDetails = async (userMappingFileId, fileInfo) => {
    setLoading(true);
    setError(null);

    try {
      const corporationId =
        selectedCorporation?.value || fileInfo?.corporationId || "1";

      const [detailsResponse, commonColumnsData] = await Promise.all([
        axiosClient.get(
          `/api/Mapper/GetMapperFileDetails?userMappingFileId=${userMappingFileId}`
        ),
        fetchCommonColumns(corporationId, userMappingFileId),
      ]);

      if (detailsResponse && detailsResponse.data) {
        const data = Array.isArray(detailsResponse.data)
          ? detailsResponse.data
          : [];
        processGraphData(data, fileInfo, commonColumnsData);
      } else {
        setError("Failed to fetch mapper file details");
        toast.error("Failed to fetch mapper file details");
      }
    } catch (err) {
      const errorMessage = err.message || "Failed to fetch mapper file details";
      setError(errorMessage);
      console.error("Error fetching mapper file details:", err);
      toast.error("Error loading lineage data: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getHandleId = (angle, handleType) => {
    let normalizedAngle = (angle + Math.PI / 2) % (Math.PI * 2);
    if (normalizedAngle < 0) normalizedAngle += Math.PI * 2;
    const handleIndex = Math.round((normalizedAngle / (Math.PI * 2)) * 8) % 8;
    return `${handleType}-${handleIndex}`;
  };

  const processGraphData = useCallback(
    (details, fileInfo, commonColumnsData = []) => {
      console.log("Processing graph data:", { details, fileInfo });

      if (!details || details.length === 0) {
        setError("No mapping details found for this object");
        toast.warning("No mapping details found for this object");
        setNodes([]);
        setEdges([]);
        return;
      }

      const newNodes = [];
      const newEdges = [];
      const nodeIds = new Set();

      // Layout configuration
      const centerX = 600;
      const centerY = 400;
      const targetRadius = 400;
      const sourceRadius = 300;

      // Central node - Object
      const centralNodeId = `obj_${
        fileInfo.fileName || fileInfo.uploadedObjName || "object"
      }`;
      newNodes.push({
        id: centralNodeId,
        type: "object",
        data: {
          label:
            fileInfo.fileName ||
            fileInfo.uploadedObjDescription ||
            fileInfo.uploadedObjName ||
            "Object",
        },
        position: { x: centerX, y: centerY },
      });
      nodeIds.add(centralNodeId);

      // Group data by Target Structure
      const targetStructureMap = new Map();

      details.forEach((item) => {
        const targetStruct = item["Target Structure (MC)"];
        const sourceTable = item["Source - Table Name"];

        if (
          targetStruct &&
          targetStruct.trim() !== "" &&
          targetStruct !== "NA"
        ) {
          if (!targetStructureMap.has(targetStruct)) {
            targetStructureMap.set(targetStruct, new Set());
          }

          if (
            sourceTable &&
            sourceTable.trim() !== "" &&
            sourceTable !== "NA"
          ) {
            targetStructureMap.get(targetStruct).add(sourceTable);
          }
        }
      });

      console.log(
        "Target Structure Mapping:",
        Array.from(targetStructureMap.entries()).map(([k, v]) => [
          k,
          Array.from(v),
        ])
      );

      // Compute filtered nodes and edges based on active filters

      // Create target structure nodes
      const targetStructures = Array.from(targetStructureMap.keys());
      targetStructures.forEach((structure, idx) => {
        const angle =
          (idx / Math.max(targetStructures.length, 1)) * Math.PI * 2;
        const nodeId = `target_${structure}`;

        const targetX = centerX + Math.cos(angle) * targetRadius;
        const targetY = centerY + Math.sin(angle) * targetRadius;

        // Check if any field in this target structure is in-scope
        const isInScope = details.some(
          (item) =>
            item["Target Structure (MC)"] === structure &&
            item["In-scope"] === "Y"
        );

        newNodes.push({
          id: nodeId,
          type: "target",
          data: {
            label: structure,
            isInScope: isInScope,
          },
          position: {
            x: targetX - 65,
            y: targetY - 65,
          },
        });
        nodeIds.add(nodeId);

        // Edge from central object to target structure
        const dx = targetX - centerX;
        const dy = targetY - centerY;
        const edgeAngle = Math.atan2(dy, dx);
        const reverseAngle = Math.atan2(-dy, -dx);

        newEdges.push({
          id: `edge-obj-to-target-${nodeId}`,
          source: centralNodeId,
          target: nodeId,
          sourceHandle: getHandleId(edgeAngle, "source"),
          targetHandle: getHandleId(reverseAngle, "target"),
          type: "straight",
          animated: false,
          style: {
            stroke: "#06b6d4",
            strokeWidth: 2.5,
          },
          markerEnd: {
            type: "arrowclosed",
            color: "#06b6d4",
            width: 20,
            height: 20,
          },
          label: "MAPS_TO",
          labelStyle: {
            fill: "#1f2937",
            fontWeight: 700,
            fontSize: 11,
            fontFamily: "system-ui, -apple-system, sans-serif",
          },
          labelBgStyle: {
            fill: "white",
            fillOpacity: 0.95,
          },
          labelBgPadding: [6, 6],
          labelBgBorderRadius: 4,
        });

        // Add source table nodes
        const sourceTables = Array.from(targetStructureMap.get(structure));
        sourceTables.forEach((sourceTable, sourceIdx) => {
          const sourceNodeId = `source_${structure}_${sourceTable}`;

          const sourceAngle =
            angle +
            (sourceIdx / Math.max(sourceTables.length, 1)) * Math.PI -
            Math.PI / 2;
          const sourceX = targetX + Math.cos(sourceAngle) * sourceRadius;
          const sourceY = targetY + Math.sin(sourceAngle) * sourceRadius;

          if (!nodeIds.has(sourceNodeId)) {
            newNodes.push({
              id: sourceNodeId,
              type: "source",
              data: { label: sourceTable },
              position: {
                x: sourceX - 65,
                y: sourceY - 65,
              },
            });
            nodeIds.add(sourceNodeId);

            const sourceDx = targetX - sourceX;
            const sourceDy = targetY - sourceY;
            const sourceEdgeAngle = Math.atan2(sourceDy, sourceDx);
            const sourceReverseAngle = Math.atan2(-sourceDy, -sourceDx);

            newEdges.push({
              id: `edge-source-to-target-${sourceNodeId}`,
              source: sourceNodeId,
              target: nodeId,
              sourceHandle: getHandleId(sourceEdgeAngle, "source"),
              targetHandle: getHandleId(sourceReverseAngle, "target"),
              type: "straight",
              animated: false,
              style: {
                stroke: "#10b981",
                strokeWidth: 2.5,
              },
              markerEnd: {
                type: "arrowclosed",
                color: "#10b981",
                width: 20,
                height: 20,
              },
              label: "SOURCES_FROM",
              labelStyle: {
                fill: "#1f2937",
                fontWeight: 700,
                fontSize: 11,
                fontFamily: "system-ui, -apple-system, sans-serif",
              },
              labelBgStyle: {
                fill: "white",
                fillOpacity: 0.95,
              },
              labelBgPadding: [6, 6],
              labelBgBorderRadius: 4,
            });
          }
        });
      });

      // Add common column edges
      if (commonColumnsData && commonColumnsData.length > 0) {
        commonColumnsData.forEach((pair) => {
          const tableA = pair.table_a;
          const tableB = pair.table_b;

          const nodeA = newNodes.find(
            (node) => node.type === "source" && node.data.label === tableA
          );
          const nodeB = newNodes.find(
            (node) => node.type === "source" && node.data.label === tableB
          );

          if (nodeA && nodeB && pair.common_columns.length > 0) {
            const edgeId = `edge-common-${tableA}-${tableB}`;

            if (!newEdges.find((e) => e.id === edgeId)) {
              newEdges.push({
                id: edgeId,
                source: nodeA.id,
                target: nodeB.id,
                type: "commonColumn",
                animated: true,
                style: {
                  stroke: "#8b5cf6",
                  strokeWidth: 2,
                  strokeDasharray: "5,5",
                },
                markerEnd: {
                  type: "arrowclosed",
                  color: "#8b5cf6",
                  width: 15,
                  height: 15,
                },
                label: `${pair.common_columns.length} common`,
                labelStyle: {
                  fill: "#6b21a8",
                  fontWeight: 600,
                  fontSize: 10,
                  fontFamily: "system-ui, -apple-system, sans-serif",
                },
                labelBgStyle: {
                  fill: "#f3e8ff",
                  fillOpacity: 0.95,
                },
                labelBgPadding: [4, 6],
                labelBgBorderRadius: 3,
                data: {
                  commonColumns: pair.common_columns,
                },
              });
            }
          }
        });
      }

      console.log("Final graph data:", {
        nodes: newNodes.length,
        edges: newEdges.length,
        targetStructures: targetStructures.length,
      });

      if (newNodes.length === 1) {
        toast.info("No target structures found for this object");
      }

      setNodes(newNodes);
      setEdges(newEdges);
    },
    [setNodes, setEdges, filters]
  );

  // Compute filtered nodes and edges based on active filters
  const filteredNodes = nodes.filter((node) => {
    if (node.type === "target") {
      if (!filters.showTargets) return false;
      if (filters.showInScopeOnly && !node.data.isInScope) return false;
      return true;
    }
    if (node.type === "source") return filters.showSources;
    return true; // Always show object node
  });

  const filteredEdges = edges.filter((edge) => {
    const sourceNode = nodes.find((n) => n.id === edge.source);
    const targetNode = nodes.find((n) => n.id === edge.target);

    if (!sourceNode || !targetNode) return false;

    // Hide common column edges if filter is off
    if (edge.type === "commonColumn" && !filters.showCommonColumns)
      return false;

    // Hide edges connected to filtered out nodes
    const sourceVisible = filteredNodes.some((n) => n.id === edge.source);
    const targetVisible = filteredNodes.some((n) => n.id === edge.target);

    return sourceVisible && targetVisible;
  });

  const handleRefresh = () => {
    if (selectedFileData?.fileID) {
      fetchMapperFileDetails(selectedFileData.fileID, selectedFileData);
    }
  };

  return (
    <div className="w-full h-full flex bg-gray-50 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between h-16 z-30 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Database className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Cross-System Lineage Map
            </h2>
            {selectedFileData && (
              <p className="text-xs text-gray-500">
                {selectedFileData.fileName}
              </p>
            )}
          </div>
        </div>

       

        {/* Filter Panel */}
        {isFilterPanelOpen && (
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            onClose={() => setIsFilterPanelOpen(false)}
          />
        )}

        <div className="flex items-center gap-3">
          {selectedFileData && nodes.length > 0 && (
            <button
              type="button"
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
              className="bg-white p-2 rounded-lg shadow-md border border-gray-200 hover:bg-gray-50 transition-all flex items-center gap-2"
              title="Filter Graph Elements"
              aria-label="Filter Graph Elements"
              aria-expanded={isFilterPanelOpen}
            >
              <Filter className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Filters</span>
              {(!filters.showTargets ||
                !filters.showSources ||
                !filters.showCommonColumns ||
                filters.showInScopeOnly) && (
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
              )}
            </button>
          )}

          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading || !selectedFileData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            title="Refresh lineage data"
            aria-label="Refresh lineage data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="text-sm font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full h-full pt-16 bg-gradient-to-br from-gray-50 to-gray-100">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
            <div className="text-center">
              <Loader className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-3" />
              <p className="text-gray-600 font-medium text-sm">
                Loading lineage data...
              </p>
              {selectedFileData && (
                <p className="text-gray-400 text-xs mt-1">
                  {selectedFileData.fileName}
                </p>
              )}
            </div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-md">
              <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <p className="text-xl text-gray-700 font-medium mb-2">
                Unable to Load Lineage
              </p>
              <p className="text-sm text-gray-500 mb-4">{error}</p>
              <button
                type="button"
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                aria-label="Try again"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : nodes.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500 font-medium">
                No lineage data available
              </p>
              <p className="text-sm text-gray-400 mt-2">
                This object has no mapping details
              </p>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={filteredNodes}
            edges={filteredEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.3, minZoom: 0.4, maxZoom: 1.5 }}
            defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
            minZoom={0.2}
            maxZoom={2}
            connectionLineType="straight"
            edgesUpdatable={false}
            edgesFocusable={false}
            defaultEdgeOptions={{
              type: "straight",
              animated: false,
            }}
          >
            <Background color="#d1d5db" gap={20} size={1.5} variant="dots" />
            <Controls position="top-right" showInteractive={false} />
            {/* <MiniMap
              nodeColor={(node) => {
                switch (node.type) {
                  case 'object': return '#fb923c';
                  case 'target': return '#06b6d4';
                  case 'source': return '#10b981';
                  default: return '#94a3b8';
                }
              }}
              position="bottom-right"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid #e5e7eb',
              }}
            /> */}
          </ReactFlow>
        )}
      </div>
    </div>
  );
};

export default DataLineageViewers;
